import { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ModalNew from "components/ModalNew";
import FormikInput from "components/formik/FormikInput";
import FormikPhoneInput from "components/formik/FormikPhoneInput";
import AvatarUploader from "components/AvatarUploader";
import FormikRadioButtons from "components/formik/FormikRadioButtons";

import contactDataSchema from "libs/validations/contactDataSchema";

const PersonalDataModal = ({
  data,
  photo_progress,
  changeRole,
  updateSettings,
  updatePhoto,
  redirect,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [photo, setPhoto] = useState(null);

  const accountType = useRef(null);

  useEffect(() => {
    if (data?.type) accountType.current = data?.type;
  }, [data]);

  const handleClose = (redirectEnabled = false) => {
    if (typeof onClose === "function") onClose();

    if (!!redirect && redirectEnabled) {
      navigate(redirect, { replace: true });
    }

    setTimeout(() => {
      setFile(null);
    }, 50);
  };

  const onSubmit = async (values, setSubmitting, setErrors) => {
    try {
      if (file && typeof updatePhoto === "function") {
        await updatePhoto(file, photo);
      }

      if (typeof updateSettings === "function")
        await updateSettings(values?.contact_data);

      handleClose(values?.contact_data?.type !== accountType.current);
    } catch (err) {
      if (err?.response?.data?.type === "email_exist") {
        setErrors({ contact_data: { email: err?.response?.data?.error } });
      }
    }

    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={{
        contact_data: {
          first_name: data?.first_name,
          last_name: data?.last_name,
          email: data?.email,
          type: data?.type,
          phone: {
            code: data?.contact_data?.phone?.code || data?.phone?.code,
            number: data?.contact_data?.phone?.number || data?.phone?.number,
          },
        },
      }}
      enableReinitialize
      validationSchema={contactDataSchema}
      onSubmit={(values, { setSubmitting, setErrors }) => {
        onSubmit(values, setSubmitting, setErrors);
      }}
    >
      {({ isSubmitting, submitForm }) => (
        <ModalNew
            title={t("Change user data")}
            variant="wide"
            loading={isSubmitting}
            isOpen={isOpen}
            onClose={handleClose}
            onSubmit={submitForm}
          >
            <Form autoComplete="off">
            <div className="form form--columns">
              <div className="form__column form__column--avatar form__column--full">
                <AvatarUploader
                  data={data}
                  photo={photo}
                  photo_progress={photo_progress}
                  loading={!!file && isSubmitting}
                  disabled={isSubmitting}
                  setFile={setFile}
                  setPhoto={setPhoto}
                />
              </div>
              {changeRole && (
                <div className="form__column form__column--full">
                  <FormikRadioButtons
                    name="contact_data.type"
                    label={t("Account type")}
                    options={[
                      {
                        label: t("Administrator"),
                        value: "admin",
                        selected: data?.type === "admin",
                      },
                      {
                        label: t("Webmaster"),
                        value: "webmaster",
                        selected: data?.type === "webmaster",
                      },
                      {
                        label: t("Client"),
                        value: "client",
                        selected: data?.type === "client",
                      },
                    ]}
                    disabled={isSubmitting}
                  />
                </div>
              )}
              <div className="form__column">
                <FormikInput
                  name="contact_data.first_name"
                  label={t("Name")}
                  placeholder="John"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form__column">
                <FormikInput
                  name="contact_data.last_name"
                  label={t("Surname")}
                  placeholder="Doe"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form__column">
                <FormikInput
                  name="contact_data.email"
                  label={t("Email address")}
                  disabled={isSubmitting}
                  placeholder="j.doe@domain.com"
                />
              </div>
              <div className="form__column">
                <FormikPhoneInput
                  label={t("Phone number")}
                  name="contact_data.phone"
                  placeholder="123 456 123"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            </Form>
          </ModalNew>
      )}
    </Formik>
  );
};

export default PersonalDataModal;
